import SurveyService, {
  SurveyTemplate,
  SurveyTemplateWithId,
} from '../../../services/SurveyService';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useActiveProjectId, useNotify, useServiceAuth } from '../../../hooks';
import { defineMessages, useIntl } from 'react-intl';

export const messages = defineMessages({
  listSurveyTemplatesError: {
    id: 'surveyTemplates.listSurveyTemplatesError',
    defaultMessage: 'Error listing survey templates',
  },
  createSurveyTemplateSuccess: {
    id: 'surveyTemplates.createSurveyTemplateSuccess',
    defaultMessage: 'Survey template created successfully',
  },
  createSurveyTemplateError: {
    id: 'surveyTemplates.createSurveyTemplateError',
    defaultMessage: 'Error creating survey template',
  },
  updateSurveyTemplateSuccess: {
    id: 'surveyTemplates.updateSurveyTemplateSuccess',
    defaultMessage: 'Survey template updated successfully',
  },
  updateSurveyTemplateError: {
    id: 'surveyTemplates.updateSurveyTemplateError',
    defaultMessage: 'Error updating survey template',
  },
  deleteSurveyTemplateSuccess: {
    id: 'surveyTemplates.deleteSurveyTemplateSuccess',
    defaultMessage: 'Survey template deleted successfully',
  },
  deleteSurveyTemplateError: {
    id: 'surveyTemplates.deleteSurveyTemplateError',
    defaultMessage: 'Error deleting survey template',
  },
});

export type UseSurveyTemplatesParams = {
  surveyId?: string;
};

export type UseSurveyTemplatesReturnValue = {
  isInitializing: boolean;
  isLoading: boolean;
  surveyTemplates: SurveyTemplateWithId[];
  createSurveyTemplate: (template: SurveyTemplate) => void;
  updateSurveyTemplate: (surveyTemplate: SurveyTemplateWithId) => void;
  deleteSurveyTemplate: (templateId: string) => void;
};

const surveyService = new SurveyService();

export const useSurveyTemplates = ({
  surveyId,
}: UseSurveyTemplatesParams): UseSurveyTemplatesReturnValue => {
  const serviceAuth = useServiceAuth();
  const activeProjectId = useActiveProjectId();
  const queryKey = ['surveyTemplates', surveyId];
  const { formatMessage } = useIntl();
  const [notify] = useNotify();
  const queryClient = useQueryClient();
  const surveyTemplatesQuery = useQuery(
    queryKey,
    async () => {
      if (!surveyId) return Promise.resolve([]);

      const response = await surveyService.listSurveyTemplates(serviceAuth)(
        activeProjectId,
        surveyId,
      );
      return response.data;
    },
    {
      onError: () => {
        notify(formatMessage(messages.listSurveyTemplatesError), 'error');
      },
    },
  );
  const updateTemplateMutation = useMutation(
    async (template: SurveyTemplateWithId) => {
      const { templateId, ...rest } = template;
      return surveyService.updateSurveyTemplate(serviceAuth)(
        activeProjectId,
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        surveyId,
        templateId,
        rest,
      );
    },
    {
      onSuccess: (_data, args) => {
        notify(formatMessage(messages.updateSurveyTemplateSuccess), 'success');
        queryClient.setQueryData<SurveyTemplateWithId[]>(queryKey, oldData =>
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          oldData.map(_ => (_.templateId === args.templateId ? args : _)),
        );
      },
      onError: () => {
        notify(formatMessage(messages.updateSurveyTemplateError), 'error');
      },
    },
  );
  const deleteTemplateMutation = useMutation(
    async (templateId: string) => {
      return surveyService.deleteSurveyTemplate(serviceAuth)(
        activeProjectId,
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        surveyId,
        templateId,
      );
    },
    {
      onSuccess: (_data, args) => {
        notify(formatMessage(messages.deleteSurveyTemplateSuccess), 'success');
        queryClient.setQueryData<SurveyTemplateWithId[]>(queryKey, oldData =>
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          oldData.filter(_ => _.templateId !== args),
        );
      },
      onError: () => {
        notify(formatMessage(messages.deleteSurveyTemplateError), 'error');
      },
    },
  );
  const createTemplateMutation = useMutation(
    async (template: SurveyTemplate) => {
      return surveyService.createSurveyTemplate(serviceAuth)(
        activeProjectId,
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        surveyId,
        template,
      );
    },
    {
      onSuccess: (data, args) => {
        notify(formatMessage(messages.createSurveyTemplateSuccess), 'success');
        queryClient.setQueryData<SurveyTemplateWithId[]>(queryKey, oldData => [
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          ...oldData,
          { ...args, templateId: data.data.templateId },
        ]);
      },
      onError: () => {
        notify(formatMessage(messages.createSurveyTemplateError), 'error');
      },
    },
  );

  return {
    isInitializing:
      surveyTemplatesQuery.isLoading || surveyTemplatesQuery.isFetching,
    isLoading:
      surveyTemplatesQuery.isLoading ||
      updateTemplateMutation.isLoading ||
      deleteTemplateMutation.isLoading,
    surveyTemplates: surveyTemplatesQuery.data || [],
    createSurveyTemplate: createTemplateMutation.mutate,
    updateSurveyTemplate: updateTemplateMutation.mutate,
    deleteSurveyTemplate: deleteTemplateMutation.mutate,
  };
};
